import { useId, useState, type SubmitEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { api, errorMessage } from '../api';
import { AuthError, AuthShell } from './AuthShell';

interface LoginPageProps {
  /** `verify`: the account has 2FA on and must enter its code next. */
  onSuccess: (mfaStep: 'verify' | null) => void;
}

export function LoginPage({ onSuccess }: LoginPageProps) {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const id = useId();

  async function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      const { mfaStep } = await api.login(identifier.trim(), password);
      onSuccess(mfaStep);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthShell title="Đăng nhập" description="Dùng tài khoản quản trị được cấp cho bạn.">
      <form className="grid gap-4" onSubmit={submit} noValidate>
        <div className="grid gap-2">
          <Label htmlFor={`${id}-identifier`}>Tên đăng nhập hoặc email</Label>
          <Input
            id={`${id}-identifier`}
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            value={identifier}
            onChange={(event) => setIdentifier(event.target.value)}
            required
            autoFocus
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor={`${id}-password`}>Mật khẩu</Label>
          <Input
            id={`${id}-password`}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
        </div>
        {error && <AuthError>{error}</AuthError>}
        <Button
          type="submit"
          size="lg"
          className="h-11"
          disabled={pending || !identifier.trim() || !password}
        >
          {pending ? 'Đang kiểm tra…' : 'Đăng nhập'}
        </Button>
      </form>
    </AuthShell>
  );
}
